/**
 * UTM Tracking Module
 * 
 * Provides end-to-end UTM tracking with first-touch and latest-touch attribution.
 * Persists UTM data to localStorage with 30-day TTL.
 */

// UTM parameter keys we track
export const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const;
export type UTMParam = typeof UTM_PARAMS[number];

// Ad click ID parameters (for offline conversion tracking)
export const CLICK_ID_PARAMS = ['gclid', 'fbclid', 'msclkid'] as const;
export type ClickIdParam = typeof CLICK_ID_PARAMS[number];

// All tracked params combined
const ALL_TRACKED_PARAMS = [...UTM_PARAMS, ...CLICK_ID_PARAMS] as const;

// Storage keys
const STORAGE_KEYS = {
  FIRST_TOUCH: 'utm_first_touch',
  LATEST_TOUCH: 'utm_latest_touch',
  LANDING_PAGE: 'utm_landing_page',
  REFERRER: 'utm_original_referrer',
  EXPIRY: 'utm_expiry',
  REF_CODE: 'utm_ref_code',
} as const;

// 30 days in milliseconds
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

export interface UTMData {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_content?: string;
  utm_term?: string;
  // Ad click IDs
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
}

export interface AttributionPayload {
  // Latest touch UTMs
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content: string | null;
  utm_term: string | null;
  // First touch UTMs (prefixed with ft_)
  ft_utm_source: string | null;
  ft_utm_medium: string | null;
  ft_utm_campaign: string | null;
  ft_utm_content: string | null;
  ft_utm_term: string | null;
  // Ad click IDs (first-touch wins for attribution)
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  // Metadata
  landing_page: string | null;
  current_page: string;
  referrer: string | null;
  timestamp: string;
  // Referral code
  ref_code: string | null;
}

/**
 * Check if localStorage is available
 */
function isStorageAvailable(): boolean {
  try {
    const test = '__storage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
}

/**
 * Check if UTM data has expired (30 days)
 */
function isExpired(): boolean {
  if (!isStorageAvailable()) return true;
  
  const expiry = localStorage.getItem(STORAGE_KEYS.EXPIRY);
  if (!expiry) return true;
  
  return Date.now() > parseInt(expiry, 10);
}

/**
 * Refresh the TTL expiry
 */
function refreshExpiry(): void {
  if (!isStorageAvailable()) return;
  localStorage.setItem(STORAGE_KEYS.EXPIRY, String(Date.now() + TTL_MS));
}

/**
 * Clear all UTM data if expired
 */
function clearIfExpired(): void {
  if (isExpired()) {
    if (isStorageAvailable()) {
      Object.values(STORAGE_KEYS).forEach(key => localStorage.removeItem(key));
    }
  }
}

/**
 * Parse UTM parameters from the current URL
 */
export function parseUTMsFromURL(): UTMData {
  const params = new URLSearchParams(window.location.search);
  const utms: UTMData = {};
  
  for (const param of ALL_TRACKED_PARAMS) {
    const value = params.get(param);
    if (value) {
      utms[param] = value;
    }
  }
  
  return utms;
}

/**
 * Check if UTM data object has any values
 */
export function hasUTMData(utms: UTMData): boolean {
  return Object.values(utms).some(v => v !== undefined && v !== '');
}

/**
 * Get stored UTM data from localStorage
 */
function getStoredUTMs(key: string): UTMData {
  if (!isStorageAvailable()) return {};
  
  clearIfExpired();
  
  try {
    const stored = localStorage.getItem(key);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Save UTM data to localStorage
 */
function saveUTMs(key: string, utms: UTMData): void {
  if (!isStorageAvailable()) return;
  if (!hasUTMData(utms)) return;
  
  try {
    localStorage.setItem(key, JSON.stringify(utms));
    refreshExpiry();
  } catch {
    console.warn('Failed to save UTM data to localStorage');
  }
}

/**
 * Save first-touch UTMs (only if not already saved)
 */
export function saveFirstTouchUTMs(utms: UTMData): void {
  if (!hasUTMData(utms)) return;
  
  const existing = getStoredUTMs(STORAGE_KEYS.FIRST_TOUCH);
  if (!hasUTMData(existing)) {
    saveUTMs(STORAGE_KEYS.FIRST_TOUCH, utms);
    console.log('[UTM] First touch saved:', utms);
  }
}

/**
 * Save latest-touch UTMs (always overwrites)
 */
export function saveLatestTouchUTMs(utms: UTMData): void {
  if (!hasUTMData(utms)) return;
  
  saveUTMs(STORAGE_KEYS.LATEST_TOUCH, utms);
  console.log('[UTM] Latest touch saved:', utms);
}

/**
 * Get first-touch UTMs
 */
export function getFirstTouchUTMs(): UTMData {
  return getStoredUTMs(STORAGE_KEYS.FIRST_TOUCH);
}

/**
 * Get latest-touch UTMs
 */
export function getLatestTouchUTMs(): UTMData {
  return getStoredUTMs(STORAGE_KEYS.LATEST_TOUCH);
}

/**
 * Get effective UTMs (URL > latest stored > first touch)
 * Priority: Current URL params take precedence over stored values
 */
export function getEffectiveUTMs(): UTMData {
  const urlUTMs = parseUTMsFromURL();
  const latestUTMs = getLatestTouchUTMs();
  const firstTouchUTMs = getFirstTouchUTMs();
  
  // Merge with priority: URL > latest > first touch
  const effective: UTMData = {};
  
  for (const param of UTM_PARAMS) {
    effective[param] = urlUTMs[param] || latestUTMs[param] || firstTouchUTMs[param];
  }
  
  // For click IDs, first-touch wins (Google/Meta attribute to first click in window)
  for (const param of CLICK_ID_PARAMS) {
    effective[param] = firstTouchUTMs[param] || latestUTMs[param] || urlUTMs[param];
  }
  
  return effective;
}

/**
 * Save landing page URL (only once per session/visit)
 */
export function saveLandingPage(): void {
  if (!isStorageAvailable()) return;
  
  clearIfExpired();
  
  const existing = localStorage.getItem(STORAGE_KEYS.LANDING_PAGE);
  if (!existing) {
    const landingPage = window.location.href;
    localStorage.setItem(STORAGE_KEYS.LANDING_PAGE, landingPage);
    refreshExpiry();
    console.log('[UTM] Landing page saved:', landingPage);
  }
}

/**
 * Save original referrer (only once)
 */
export function saveReferrer(): void {
  if (!isStorageAvailable()) return;
  
  clearIfExpired();
  
  const existing = localStorage.getItem(STORAGE_KEYS.REFERRER);
  if (!existing && document.referrer) {
    localStorage.setItem(STORAGE_KEYS.REFERRER, document.referrer);
    refreshExpiry();
    console.log('[UTM] Referrer saved:', document.referrer);
  }
}

/**
 * Get stored landing page
 */
export function getLandingPage(): string | null {
  if (!isStorageAvailable()) return null;
  clearIfExpired();
  return localStorage.getItem(STORAGE_KEYS.LANDING_PAGE);
}

/**
 * Get stored referrer
 */
export function getStoredReferrer(): string | null {
  if (!isStorageAvailable()) return null;
  clearIfExpired();
  return localStorage.getItem(STORAGE_KEYS.REFERRER);
}

/**
 * Parse and save the `ref` (referral code) parameter from the current URL.
 * Only saves once — first ref seen wins.
 */
export function saveRefCode(): void {
  if (!isStorageAvailable()) return;
  
  const params = new URLSearchParams(window.location.search);
  const ref = params.get('ref');
  if (!ref) return;
  
  const existing = localStorage.getItem(STORAGE_KEYS.REF_CODE);
  if (!existing) {
    localStorage.setItem(STORAGE_KEYS.REF_CODE, ref);
    refreshExpiry();
    console.log('[UTM] Referral code saved:', ref);
  }
}

/**
 * Get stored referral code
 */
export function getRefCode(): string | null {
  if (!isStorageAvailable()) return null;
  clearIfExpired();
  return localStorage.getItem(STORAGE_KEYS.REF_CODE);
}

/**
 * Build a query string from UTM data
 */
export function buildUTMQueryString(utms: UTMData): string {
  const params = new URLSearchParams();
  
  for (const [key, value] of Object.entries(utms)) {
    if (value) {
      params.set(key, value);
    }
  }
  
  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
}

/**
 * Append UTMs to an internal link URL
 * Returns the original URL if it's external, mailto:, tel:, download, or anchor-only
 */
export function appendUTMsToInternalLink(href: string, utms?: UTMData): string {
  // Use effective UTMs if not provided
  const effectiveUTMs = utms || getEffectiveUTMs();
  
  // Skip if no UTMs to append
  if (!hasUTMData(effectiveUTMs)) {
    return href;
  }
  
  // Skip external links
  if (href.startsWith('http://') || href.startsWith('https://')) {
    try {
      const url = new URL(href);
      const currentHost = window.location.host;
      // Only append to same-domain links
      if (url.host !== currentHost) {
        return href;
      }
    } catch {
      return href;
    }
  }
  
  // Skip special protocols
  if (
    href.startsWith('mailto:') ||
    href.startsWith('tel:') ||
    href.startsWith('javascript:') ||
    href.startsWith('#') ||
    href.startsWith('data:') ||
    href.includes('.pdf') ||
    href.includes('.doc') ||
    href.includes('.csv')
  ) {
    return href;
  }
  
  // Parse existing URL
  try {
    const base = window.location.origin;
    const url = new URL(href, base);
    
    // Add UTM params (don't override existing ones)
    for (const [key, value] of Object.entries(effectiveUTMs)) {
      if (value && !url.searchParams.has(key)) {
        url.searchParams.set(key, value);
      }
    }
    
    // Return path + search (without origin for relative URLs)
    if (href.startsWith('/') || !href.includes('://')) {
      return url.pathname + url.search + url.hash;
    }
    
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * Get full attribution payload for form submission
 * Includes both first-touch and latest-touch UTMs, plus metadata
 */
export function getAttributionPayload(): AttributionPayload {
  const effective = getEffectiveUTMs();
  const firstTouch = getFirstTouchUTMs();
  
  return {
    // Latest/effective touch UTMs
    utm_source: effective.utm_source || null,
    utm_medium: effective.utm_medium || null,
    utm_campaign: effective.utm_campaign || null,
    utm_content: effective.utm_content || null,
    utm_term: effective.utm_term || null,
    // First touch UTMs (prefixed with ft_)
    ft_utm_source: firstTouch.utm_source || null,
    ft_utm_medium: firstTouch.utm_medium || null,
    ft_utm_campaign: firstTouch.utm_campaign || null,
    ft_utm_content: firstTouch.utm_content || null,
    ft_utm_term: firstTouch.utm_term || null,
    // Ad click IDs (first-touch wins)
    gclid: effective.gclid || null,
    fbclid: effective.fbclid || null,
    msclkid: effective.msclkid || null,
    // Metadata
    landing_page: getLandingPage(),
    current_page: window.location.href,
    referrer: getStoredReferrer() || document.referrer || null,
    timestamp: new Date().toISOString(),
    // Referral code
    ref_code: getRefCode(),
  };
}

/**
 * Initialize UTM tracking on page load
 * Should be called once when the app mounts
 */
export function initializeUTMTracking(): void {
  // Save landing page and referrer (first visit only)
  saveLandingPage();
  saveReferrer();
  
  // Save referral code from URL (first ref wins)
  saveRefCode();
  
  // Parse UTMs from current URL
  const urlUTMs = parseUTMsFromURL();
  
  if (hasUTMData(urlUTMs)) {
    // Save as first touch if not already set
    saveFirstTouchUTMs(urlUTMs);
    // Always update latest touch
    saveLatestTouchUTMs(urlUTMs);
  }
  
  console.log('[UTM] Tracking initialized:', {
    urlUTMs,
    firstTouch: getFirstTouchUTMs(),
    latestTouch: getLatestTouchUTMs(),
    landingPage: getLandingPage(),
  });
}

/**
 * Debug helper: Get all stored UTM data
 */
export function debugUTMData(): Record<string, unknown> {
  return {
    firstTouch: getFirstTouchUTMs(),
    latestTouch: getLatestTouchUTMs(),
    effective: getEffectiveUTMs(),
    urlParams: parseUTMsFromURL(),
    landingPage: getLandingPage(),
    referrer: getStoredReferrer(),
    isExpired: isExpired(),
    payload: getAttributionPayload(),
  };
}

// Expose debug helper to window for testing
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).debugUTM = debugUTMData;
}
